import React, { useState, useRef, useEffect, useCallback } from "react";
import { ArrowLeft, Download, Save, Palette, Check, BookOpen, ChevronDown, LayoutGrid, Lock, PackagePlus } from "lucide-react";
import CatalogPicker from "./CatalogPicker";
import jsPDF from "jspdf";
import html2canvas from "html2canvas-pro";
import A4Document from "./A4Document";
import { GoogleDots } from "./GoogleBits";
import { saveQuote, updateQuote, editQuote, renderQuotePdf } from "../lib/api";
import { usePlan } from "../context/PlanContext";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { Sparkles, MessageSquare, ArrowUp, X as XIcon, Paperclip, Undo2 } from "lucide-react";

const LAYOUTS = [
  { id: "minimal_classic", name: "Clássico" },
  { id: "modern_editorial", name: "Editorial" },
  { id: "corporate_bold", name: "Corporativo" },
  { id: "compact_tech", name: "Técnico" },
  { id: "creative_studio", name: "Criativo" },
  { id: "glass_aurora", name: "Aurora" },
  { id: "gradient_wave", name: "Gradiente" },
  { id: "soft_pastel", name: "Pastel" },
  { id: "mono_grid", name: "Grade" },
  { id: "prestige_dark", name: "Prestígio" },
];

const COLORS = ["#0f172a", "#4A3BFA", "#059669", "#4f46e5", "#e11d48", "#d97706"];

// Splits a tall rendered sheet (canvas) into page segments, cutting ONLY inside blank horizontal bands of the
// actual rendered image, so no text/row is ever sliced. Works on the html2canvas output itself (no DOM mismatch).
const PAGE_PAD_TOP = 40;
const PAGE_PAD_BOTTOM = 36;
export function computeCanvasCuts(canvas, scale, pagePx) {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const W = canvas.width;
  const totalCss = Math.floor(canvas.height / scale);
  const xs = [];
  for (let x = Math.round(4 * scale); x < W - 4 * scale; x += Math.max(1, Math.round(2 * scale))) xs.push(x);
  const rowCache = new Map();
  const uniformRow = (yCss) => {
    if (rowCache.has(yCss)) return rowCache.get(yCss);
    const y = Math.min(canvas.height - 1, Math.round(yCss * scale + scale / 2));
    const row = ctx.getImageData(0, y, W, 1).data;
    const r0 = row[xs[0] * 4], g0 = row[xs[0] * 4 + 1], b0 = row[xs[0] * 4 + 2];
    let ok = true;
    for (let i = 1; i < xs.length; i++) {
      const o = xs[i] * 4;
      if (Math.abs(row[o] - r0) > 10 || Math.abs(row[o + 1] - g0) > 10 || Math.abs(row[o + 2] - b0) > 10) { ok = false; break; }
    }
    rowCache.set(yCss, ok);
    return ok;
  };
  const segs = [];
  let start = 0;
  let firstPage = true;
  while (start < totalCss - 1) {
    const padTop = firstPage ? 0 : PAGE_PAD_TOP;
    const usable = pagePx - padTop - PAGE_PAD_BOTTOM;
    let end = Math.min(start + usable, totalCss);
    if (end < totalCss) {
      // scan upward for the first blank band at least 6 css px tall; cut in its middle
      let cut = -1;
      const minY = Math.max(start + 120, end - 420);
      let run = 0;
      for (let y = end; y >= minY; y--) {
        if (uniformRow(y)) {
          run++;
          if (run >= 6) { cut = y + Math.floor(run / 2); break; }
        } else {
          run = 0;
        }
      }
      if (cut > start + 120) end = cut;
    }
    segs.push({ start, end, padTop });
    start = end;
    firstPage = false;
  }
  return segs;
}

export default function QuoteEditor({ profile, companyId, existing, initialData, onBack, onSaved }) {
  const { limits, isAllowedModel, canPersonalize, openUpgrade } = usePlan();
  const src = existing?.theme || {};
  const [layout, setLayout] = useState(existing?.layout || profile?.layout || "minimal_classic");
  const [data, setData] = useState(existing?.data || initialData || {});
  const [pickerOpen, setPickerOpen] = useState(false);
  const addCatalogItem = (line) => {
    setData((d) => ({ ...d, items: [...(d.items || []).filter((it) => (it.description || "").trim() || it.unit_price), line] }));
    toast.success(line.unit_price ? "Item adicionado ao orçamento" : "Item adicionado — informe o valor");
  };
  const [company] = useState(existing?.company || { ...profile?.company, logo: profile?.logo });
  const [primary, setPrimary] = useState(src.primary || profile?.colors?.primary || "#0f172a");
  const [font, setFont] = useState(src.font || profile?.font || "sans");
  const [cover, setCover] = useState(src.cover ?? profile?.doc?.cover ?? false);
  const [headerLabel, setHeaderLabel] = useState(src.headerLabel ?? profile?.doc?.headerLabel ?? "Orçamento");
  const [coverTitle, setCoverTitle] = useState(src.coverTitle ?? profile?.doc?.coverTitle ?? "Proposta Comercial");
  const [logoRight, setLogoRight] = useState(src.logoRight ?? profile?.doc?.logoRight ?? false);
  const [logoSize, setLogoSize] = useState(src.logoSize || profile?.doc?.logoSize || "md");
  const [coverImage, setCoverImage] = useState(src.coverImage ?? "");
  const [coverBg, setCoverBg] = useState(src.coverBg ?? "");
  const [chatOpen, setChatOpen] = useState(true);
  const [chatMsgs, setChatMsgs] = useState([{ role: "assistant", content: "Seu orçamento está pronto! Se quiser mudar qualquer coisa — texto, valores, cor, fonte, modelo ou capa — é só me pedir aqui." }]);
  const [chatInput, setChatInput] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [status] = useState(existing?.status || "Rascunho");
  const [quoteId, setQuoteId] = useState(existing?.id || null);
  const [saving, setSaving] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [showColors, setShowColors] = useState(false);
  const [showLayouts, setShowLayouts] = useState(false);
  const [scale, setScale] = useState(1);
  const [docH, setDocH] = useState(1123);

  const wrapRef = useRef(null);
  const sheetRef = useRef(null);
  const fileInputRef = useRef(null);

  const theme = { primary, font, cover, coverTitle, headerLabel, logoRight, logoSize, coverImage, coverBg };

  const resize = useCallback(() => {
    const w = wrapRef.current?.clientWidth || 794;
    setScale(Math.min(1, w / 794));
  }, []);
  useEffect(() => {
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [resize]);

  useEffect(() => {
    const id = setTimeout(resize, 60);
    return () => clearTimeout(id);
  }, [chatOpen, resize]);

  useEffect(() => {
    const el = sheetRef.current;
    if (!el) return;
    const update = () => setDocH(el.scrollHeight || 1123);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [layout, cover, data]);

  const snapshot = () => ({ data, layout, primary, font, cover, coverTitle, headerLabel, logoRight, logoSize, coverImage, coverBg });
  const pushHistory = () => setHistory((h) => [...h.slice(-19), snapshot()]);
  const restore = (s) => {
    setData(s.data); setLayout(s.layout); setPrimary(s.primary); setFont(s.font);
    setCover(s.cover); setCoverTitle(s.coverTitle); setHeaderLabel(s.headerLabel); setLogoRight(s.logoRight); setLogoSize(s.logoSize || "md"); setCoverImage(s.coverImage); setCoverBg(s.coverBg ?? "");
  };
  const undo = () => setHistory((h) => {
    if (!h.length) return h;
    restore(h[h.length - 1]);
    setChatMsgs((m) => [...m, { role: "assistant", content: "Desfeito! Voltei para a versão anterior." }]);
    return h.slice(0, -1);
  });

  const onPickImage = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Selecione um arquivo de imagem");
    const reader = new FileReader();
    reader.onload = (e) => {
      pushHistory();
      setData((d) => ({ ...d, image_url: e.target.result }));
      setChatMsgs((m) => [...m, { role: "user", content: "Imagem enviada 📎" }, { role: "assistant", content: "Imagem adicionada ao orçamento! Se quiser, posso removê-la ou trocar por outra." }]);
    };
    reader.readAsDataURL(file);
  };

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatBusy) return;
    pushHistory();
    setChatMsgs((m) => [...m, { role: "user", content: text }]);
    setChatInput("");
    setChatBusy(true);
    try {
      const res = await editQuote({ instruction: text, data, theme, layout });
      if (res.quote) setData(res.quote);
      if (res.layout) { if (isAllowedModel(res.layout)) setLayout(res.layout); else openUpgrade("model"); }
      const th = res.theme || {};
      if (th.primary) setPrimary(th.primary);
      if (th.font) setFont(th.font);
      if (typeof th.cover === "boolean") { if (th.cover && !canPersonalize) openUpgrade("personalization"); else setCover(th.cover); }
      if (th.coverTitle) setCoverTitle(th.coverTitle);
      if (th.headerLabel) setHeaderLabel(th.headerLabel);
      if (typeof th.logoRight === "boolean") setLogoRight(th.logoRight);
      if (["sm", "md", "lg"].includes(th.logoSize)) setLogoSize(th.logoSize);
      if (th.coverImage !== undefined) setCoverImage(th.coverImage);
      if (th.coverBg !== undefined) setCoverBg(th.coverBg);
      setChatMsgs((m) => [...m, { role: "assistant", content: res.message || "Pronto!" }]);
    } catch {
      setChatMsgs((m) => [...m, { role: "assistant", content: "Não consegui aplicar agora. Pode tentar de novo?" }]);
    } finally {
      setChatBusy(false);
    }
  };

  const buildPayload = () => ({
    company_id: companyId,
    title: data.title || "Orçamento sem título",
    client_name: data.client_name || "",
    status,
    layout,
    company,
    theme,
    data,
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = buildPayload();
      let res;
      if (quoteId) {
        res = await updateQuote(quoteId, payload);
      } else {
        res = await saveQuote(payload);
        setQuoteId(res.id);
      }
      toast.success("Orçamento salvo");
      onSaved && onSaved(res);
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "limit_quotes") openUpgrade("quotes");
      else if (code === "premium_model") openUpgrade("model");
      else if (code === "limit_companies") openUpgrade("companies");
      else toast.error("Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const pdfFileName = () => {
    const client = (data.client_name || "").trim();
    const base = client ? `Proposta para ${client}` : (data.title ? `Proposta - ${data.title}` : "Proposta");
    return `${base.replace(/[\\/:*?"<>|]+/g, "").replace(/\s+/g, " ").trim() || "Proposta"}.pdf`;
  };

  const handleDownload = async () => {
    setCapturing(true);
    try {
      const blob = await renderQuotePdf({ company, layout, theme, data });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = pdfFileName(); document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("PDF gerado");
      setCapturing(false);
      return;
    } catch (e) {
      console.warn("vector PDF unavailable, falling back to raster:", e?.response?.status || e?.message);
    }
    await new Promise((r) => setTimeout(r, 150));
    try {
      const pages = Array.from(sheetRef.current.querySelectorAll(".a4-sheet"));
      const pdf = new jsPDF("p", "mm", "a4");
      const pageW = 210;
      const pageH = 297;
      const A4_PX = 1123;
      const RENDER_SCALE = (typeof window !== "undefined" && window.innerWidth < 768) ? 2 : 3; // high DPI on desktop, guard mobile memory
      for (let p = 0; p < pages.length; p++) {
        const el = pages[p];
        const canvas = await html2canvas(el, {
          scale: RENDER_SCALE,
          backgroundColor: null,
          useCORS: true,
          imageTimeout: 0,
          ignoreElements: (node) => node.classList?.contains?.("pdf-exclude"),
        });
        if (p > 0) pdf.addPage();
        const cssH = canvas.height / RENDER_SCALE;
        if (cssH <= A4_PX + 2) {
          const imgH = (canvas.height * pageW) / canvas.width;
          pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, pageW, imgH, undefined, "FAST");
          continue;
        }
        // Long sheet: cut only between atomic blocks (table rows / leaf text) so nothing is sliced mid-content.
        const cuts = computeCanvasCuts(canvas, RENDER_SCALE, A4_PX);
        const bg = getComputedStyle(el).backgroundColor || "#fff";
        let first = true;
        for (const seg of cuts) {
          if (!first) pdf.addPage();
          const pageCanvas = document.createElement("canvas");
          pageCanvas.width = canvas.width;
          pageCanvas.height = A4_PX * RENDER_SCALE;
          const ctx = pageCanvas.getContext("2d");
          ctx.fillStyle = bg === "rgba(0, 0, 0, 0)" ? "#ffffff" : bg;
          ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
          const sy = seg.start * RENDER_SCALE;
          const sh = (seg.end - seg.start) * RENDER_SCALE;
          ctx.drawImage(canvas, 0, sy, canvas.width, sh, 0, seg.padTop * RENDER_SCALE, canvas.width, sh);
          pdf.addImage(pageCanvas.toDataURL("image/png"), "PNG", 0, 0, pageW, pageH, undefined, "FAST");
          first = false;
        }
      }
      pdf.save(pdfFileName());
      toast.success("PDF gerado");
    } catch (e) {
      console.error("PDF generation failed:", e);
      toast.error("Erro ao gerar PDF");
    } finally {
      setCapturing(false);
    }
  };

  return (
    <div className="min-h-screen pt-14 pb-20 bg-[#fafafa]">
      {/* toolbar */}
      <div className="sticky top-14 z-30 bg-[#fafafa]/85 backdrop-blur-md border-b border-slate-200/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-3">
          <button onClick={onBack} data-testid="editor-back" className="inline-flex items-center gap-1.5 text-[13px] text-slate-500 hover:text-slate-900 transition-colors shrink-0">
            <ArrowLeft size={16} /> <span className="hidden sm:inline">Voltar</span>
          </button>

          <div className="flex-1 flex justify-center min-w-0">
            <div className="relative">
              <button
                onClick={() => { setShowLayouts((s) => !s); setShowColors(false); }}
                data-testid="layout-menu-toggle"
                className="inline-flex items-center gap-2 h-9 pl-3 pr-2.5 rounded-full bg-white ring-1 ring-slate-200 hover:ring-slate-300 transition-all text-[13px] font-medium text-slate-700"
              >
                <LayoutGrid size={15} className="text-slate-500" />
                <span className="hidden sm:inline text-slate-400">Modelo:</span>
                <span className="max-w-[110px] truncate">{(LAYOUTS.find((l) => l.id === layout) || {}).name || "Modelo"}</span>
                <ChevronDown size={15} className={`text-slate-400 transition-transform ${showLayouts ? "rotate-180" : ""}`} />
              </button>
              {showLayouts && (
                <div className="fixed sm:absolute top-[110px] sm:top-full left-1/2 -translate-x-1/2 sm:mt-2 w-[calc(100vw-24px)] max-w-[300px] bg-white ring-1 ring-slate-200 rounded-2xl p-2 shadow-xl z-50 grid grid-cols-2 gap-1" data-testid="layout-menu">
                  {LAYOUTS.map((l) => {
                    const locked = !isAllowedModel(l.id);
                    return (
                      <button
                        key={l.id}
                        onClick={() => { setShowLayouts(false); if (locked) return openUpgrade("model"); setLayout(l.id); }}
                        data-testid={`switch-layout-${l.id}`}
                        className={`flex items-center justify-between text-[12.5px] font-medium px-3 py-2 rounded-xl text-left transition-all ${layout === l.id ? "bg-slate-900 text-white" : locked ? "text-slate-400 hover:bg-slate-50" : "text-slate-600 hover:bg-slate-100"}`}
                      >
                        <span className="truncate">{l.name}</span>
                        {layout === l.id ? <Check size={13} strokeWidth={3} className="shrink-0 ml-1" /> : locked ? <Lock size={12} className="shrink-0 ml-1" data-testid={`layout-lock-${l.id}`} /> : null}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <div className="relative">
              <button
                onClick={() => { setShowColors((s) => !s); setShowLayouts(false); }}
                data-testid="color-toggle"
                className="grid place-items-center w-9 h-9 rounded-full bg-white ring-1 ring-slate-200 hover:ring-slate-300 transition-all"
              >
                <Palette size={16} className="text-slate-600" />
              </button>
              {showColors && (
                <div className="absolute right-0 mt-2 bg-white ring-1 ring-slate-200 rounded-2xl p-3 shadow-xl grid grid-cols-3 gap-2.5 z-40 w-[168px]">
                  {COLORS.map((c) => (
                    <button
                      key={c}
                      onClick={() => { setPrimary(c); setShowColors(false); }}
                      className="w-8 h-8 rounded-full ring-1 ring-black/5 grid place-items-center hover:scale-110 transition-transform"
                      style={{ background: c }}
                    >
                      {primary === c && <Check size={14} className="text-white" strokeWidth={3} />}
                    </button>
                  ))}
                  <div className="col-span-3 flex items-center gap-2 pt-1 border-t border-slate-100 mt-0.5">
                    <input type="color" value={primary} onChange={(e) => setPrimary(e.target.value)} className="w-8 h-8 rounded-lg cursor-pointer bg-transparent border border-slate-200" />
                    <span className="text-[11px] text-slate-400">Personalizada</span>
                  </div>
                  {company?.logo && (
                    <div className="col-span-3 pt-2 border-t border-slate-100">
                      <div className="text-[11px] text-slate-400 mb-1.5">Tamanho da logo</div>
                      <div className="flex gap-1" data-testid="logo-size-group">
                        {[["sm", "P"], ["md", "M"], ["lg", "G"]].map(([k, l]) => (
                          <button key={k} onClick={() => setLogoSize(k)} data-testid={`logo-size-${k}`} className={`flex-1 h-7 rounded-lg text-[11px] font-semibold transition-colors ${logoSize === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500 hover:text-slate-900"}`}>{l}</button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <button
              onClick={() => { if (!canPersonalize) return openUpgrade("personalization"); setCover((c) => !c); }}
              data-testid="cover-toggle"
              title="Capa e contracapa"
              className={`relative grid place-items-center w-9 h-9 rounded-full ring-1 transition-all ${cover ? "bg-slate-900 text-white ring-slate-900" : "bg-white text-slate-600 ring-slate-200 hover:ring-slate-300"}`}
            >
              <BookOpen size={16} />
              {!canPersonalize && <span className="absolute -top-1 -right-1 grid place-items-center w-4 h-4 rounded-full bg-slate-100 ring-1 ring-slate-200 text-slate-500" data-testid="cover-lock"><Lock size={9} /></span>}
            </button>

            <Button onClick={() => setPickerOpen(true)} variant="outline" data-testid="add-from-catalog-btn" title="Adicionar item do catálogo" className="rounded-full h-9 border-slate-200 text-[13px] gap-1.5">
              <PackagePlus size={15} /> <span className="hidden sm:inline">Catálogo</span>
            </Button>
            <Button onClick={handleSave} disabled={saving} variant="outline" data-testid="save-quote-btn" className="rounded-full h-9 border-slate-200 text-[13px] gap-1.5">
              {saving ? <GoogleDots /> : <><Save size={15} /> <span className="hidden sm:inline">Salvar</span></>}
            </Button>
            <Button onClick={handleDownload} disabled={capturing} data-testid="download-pdf-button" className="rounded-full h-9 bg-slate-900 hover:bg-slate-800 text-[13px] gap-1.5">
              {capturing ? <GoogleDots /> : <><Download size={15} /> <span className="hidden sm:inline">Baixar PDF</span></>}
            </Button>
          </div>
        </div>
      </div>

      {/* sheet + chat */}
      <div className="mt-6 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-6 items-start justify-center">
          {chatOpen && (
            <div className="w-full lg:w-[340px] lg:sticky lg:top-32 bg-white ring-1 ring-slate-200 rounded-2xl flex flex-col shrink-0 order-2 lg:order-1 overflow-hidden" style={{ height: "70vh", maxHeight: 620 }} data-testid="editor-chat">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                <div className="flex items-center gap-2 text-[13px] font-semibold text-slate-800">
                  <span className="grid place-items-center w-6 h-6 rounded-lg google-gradient text-white"><Sparkles size={13} /></span>
                  Assistente
                </div>
                <div className="flex items-center gap-1.5">
                  {history.length > 0 && (
                    <button onClick={undo} data-testid="chat-undo" title="Desfazer última alteração" className="inline-flex items-center gap-1 text-[12px] text-slate-500 hover:text-slate-900 transition-colors">
                      <Undo2 size={15} /> Desfazer
                    </button>
                  )}
                  <button onClick={() => setChatOpen(false)} className="text-slate-400 hover:text-slate-700" data-testid="chat-close"><XIcon size={16} /></button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto scrollbar-none p-3 space-y-3">
                {chatMsgs.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] text-[13px] leading-relaxed rounded-2xl px-3.5 py-2.5 ${m.role === "user" ? "bg-slate-900 text-white rounded-tr-md" : "bg-slate-50 ring-1 ring-slate-100 text-slate-700 rounded-tl-md"}`} data-testid={`chat-msg-${i}`}>
                      {m.content}
                    </div>
                  </div>
                ))}
                {chatBusy && <div className="flex justify-start"><div className="bg-slate-50 ring-1 ring-slate-100 rounded-2xl px-3.5 py-3"><GoogleDots /></div></div>}
              </div>
              <div className="p-3 border-t border-slate-100">
                <div className="relative">
                  <Textarea
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
                    rows={2}
                    placeholder="Ex: mude a cor, troque o modelo, mude para euros, anexe uma imagem..."
                    data-testid="chat-input"
                    className="resize-none rounded-xl border-slate-200 text-[13px] pl-11 pr-11 min-h-[60px]"
                  />
                  <button onClick={() => fileInputRef.current?.click()} data-testid="chat-attach" title="Anexar imagem" className="absolute bottom-2.5 left-2.5 grid place-items-center w-8 h-8 rounded-full ring-1 ring-slate-200 text-slate-500 hover:text-slate-800 bg-white transition-colors">
                    <Paperclip size={15} />
                  </button>
                  <button onClick={sendChat} disabled={chatBusy || !chatInput.trim()} data-testid="chat-send" className="absolute bottom-2.5 right-2.5 grid place-items-center w-8 h-8 rounded-full bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-30 transition-all">
                    <ArrowUp size={16} strokeWidth={2.4} />
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={(e) => onPickImage(e.target.files?.[0])} data-testid="chat-file-input" />
                </div>
              </div>
            </div>
          )}

          <div ref={wrapRef} className="mx-auto lg:mx-0 flex-1 order-1 lg:order-2 w-full" style={{ maxWidth: 794 }}>
            {!chatOpen && (
              <button onClick={() => setChatOpen(true)} data-testid="chat-open" className="mb-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-600 bg-white ring-1 ring-slate-200 hover:ring-slate-300 rounded-full px-3.5 py-1.5 transition-all">
                <MessageSquare size={15} /> Pedir alterações à IA
              </button>
            )}
            <div style={{ height: `${docH * scale}px` }}>
              <div style={{ transform: `scale(${scale})`, transformOrigin: "top left" }}>
                <div ref={sheetRef}>
                  <A4Document layout={layout} company={company} theme={theme} data={data} onChange={setData} editable={!capturing} branding={!!limits?.watermark} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <p className="text-center text-[12px] text-slate-400 mt-6">Clique em qualquer texto ou valor para editar, ou peça as mudanças no chat.</p>
      </div>
      <CatalogPicker open={pickerOpen} onClose={() => setPickerOpen(false)} companyId={companyId} onPick={addCatalogItem} />
    </div>
  );
}
